import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

import { lerTemaSalvo, salvarTema, type Tema } from '@/lib/preferencias';

/**
 * Tema claro, escuro ou do sistema. Decisao 13.
 *
 * O tema efetivo e escrito em `data-tema` no elemento raiz, que e o seletor que
 * tema-claro.css e tema-escuro.css consomem.
 *
 * A preferencia do sistema operacional e lida com useSyncExternalStore, e nao
 * com useState dentro de useEffect. Derivar estado em efeito provoca render em
 * cascata: o componente renderiza, o efeito roda, chama setState e forca outro
 * render. Aqui o valor efetivo e calculado durante o render, e o efeito cuida
 * apenas do efeito colateral no DOM.
 */

const CONSULTA_ESCURO = '(prefers-color-scheme: dark)';

function assinarPreferenciaDoSistema(aoMudar: () => void): () => void {
  const consulta = window.matchMedia(CONSULTA_ESCURO);

  consulta.addEventListener('change', aoMudar);

  return () => {
    consulta.removeEventListener('change', aoMudar);
  };
}

function lerPreferenciaDoSistema(): 'claro' | 'escuro' {
  return window.matchMedia(CONSULTA_ESCURO).matches ? 'escuro' : 'claro';
}

export interface ControleDeTema {
  /** A escolha do usuario, que pode ser 'sistema'. */
  tema: Tema;
  /** O tema de fato aplicado. */
  efetivo: 'claro' | 'escuro';
  definirTema: (tema: Tema) => void;
}

export function useTema(): ControleDeTema {
  const [tema, setTema] = useState<Tema>(lerTemaSalvo);

  const preferenciaDoSistema = useSyncExternalStore(
    assinarPreferenciaDoSistema,
    lerPreferenciaDoSistema,
    // Valor no servidor: sem window, assumimos claro.
    () => 'claro' as const,
  );

  // Derivado durante o render, nao em efeito.
  const efetivo: 'claro' | 'escuro' = tema === 'sistema' ? preferenciaDoSistema : tema;

  useEffect(() => {
    document.documentElement.dataset['tema'] = efetivo;
  }, [efetivo]);

  const definirTema = useCallback((proximo: Tema) => {
    setTema(proximo);
    salvarTema(proximo);
  }, []);

  return { tema, efetivo, definirTema };
}
